import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, RefreshCw, ArrowUpRight } from "lucide-react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/apiClient";
import { useFeatureFlag } from "@/hooks/useFeatureFlags";

export default function MysteryBox() {
  const enabled = useFeatureFlag("mystery_box");
  const [picks, setPicks] = useState([]);
  const [opening, setOpening] = useState(false);
  const [opened, setOpened] = useState(false);

  const draw = async () => {
    setOpening(true); setOpened(false);
    try {
      const { data } = await api.get("/mystery-box");
      setTimeout(() => { setPicks(data.picks || []); setOpened(true); setOpening(false); }, 900);
    } catch (e) {
      toast.error(formatApiError(e));
      setOpening(false);
    }
  };

  useEffect(() => { if (enabled) draw(); /* eslint-disable-next-line */ }, [enabled]);

  if (!enabled) return <Navigate to="/" replace />;

  return (
    <div data-testid="mystery-box" className="section-pad">
      <div className="container-pp text-center max-w-3xl mx-auto">
        <div className="editorial-divider mx-auto mb-6" />
        <div className="text-[11px] uppercase tracking-[0.22em] text-brand-red flex items-center justify-center gap-2"><Sparkles size={12} /> The Mystery Box</div>
        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
          className="editorial-h1 text-5xl md:text-7xl mt-4 leading-[0.95]">
          Three flavours.<br /><em className="font-display italic">Picked at random.</em>
        </motion.h1>
        <p className="mt-6 text-brand-charcoal/70 leading-relaxed">No filters. No tabs. No menu. Just three popsicles the freezer dealt you. Refresh as many times as you like.</p>

        <button onClick={draw} disabled={opening} className="btn-pill btn-dark mt-10 inline-flex" data-testid="mb-draw">
          <RefreshCw size={16} className={opening ? "animate-spin" : ""} /> {opening ? "Opening box…" : "Open another box"}
        </button>

        {opened && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="mt-14 grid sm:grid-cols-3 gap-5" data-testid="mb-picks">
            {picks.map((p, i) => (
              <motion.div key={p.id || i}
                initial={{ opacity: 0, scale: 0.9, rotate: -3 }} animate={{ opacity: 1, scale: 1, rotate: 0 }}
                transition={{ delay: i * 0.15, type: "spring", stiffness: 220, damping: 20 }}
                className="card-elevated overflow-hidden">
                <div className="aspect-[4/3] bg-brand-muted overflow-hidden">
                  {p.image_url
                    ? <img src={p.image_url} alt={p.name} className="h-full w-full object-cover" />
                    : <div className="h-full w-full grid place-items-center text-4xl" style={{ background: (p.color_hex || "#FFC93C") + "33" }}>🍦</div>}
                </div>
                <div className="p-5 text-left">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-brand-red">Pick {i + 1}</div>
                  <div className="font-display text-2xl mt-1">{p.name}</div>
                  {p.description && <p className="text-sm text-brand-charcoal/70 mt-2 line-clamp-3">{p.description}</p>}
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}

        <div className="mt-12 text-sm text-brand-charcoal/65">
          Love what came out? <Link to="/request-stock" className="underline">Ask for it at your nearest counter</Link> or <Link to="/flavour-creator" className="underline inline-flex items-center gap-1">invent your own <ArrowUpRight size={12} /></Link>
        </div>
      </div>
    </div>
  );
}
